import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';

let controller;
let appendController;

const defaultPagination = { page: 1, limit: 10, total: 0, totalPages: 0 };
const defaultStatusCounts = {
  all: 0,
  open: 0,
  pending: 0,
  solved: 0,
  spam: 0,
  assigned_to_me: 0,
  unassigned: 0,
};

const initialState = {
  tickets: [],
  loading: false,
  error: null,
  initialLoaded: false,
  pagination: defaultPagination,
  statusCounts: defaultStatusCounts,
  /** Cached ticket lists keyed by inbox/tab query (All, Unassigned, etc.) */
  cacheByListKey: {},
  lastFetchedListKey: null,
  /** In-flight non-silent list fetches (tab/page); loading stays true until this hits 0 */
  blockingListRequests: 0,
  /**
   * Soft (cached) list revalidations in flight. List can paint from cache while the
   * header count stays in a loading/skeleton state until fresh totals arrive.
   */
  statusCountRefreshRequests: 0,
  /** Infinite scroll: background page fetch in flight */
  listLoadingMore: false,
  /** No more server pages (total reached, short page, or empty append) */
  listExhausted: false,
};

function normalizeTicket(ticket) {
  const updatedAt = ticket.updatedAt ?? ticket.updated_at;
  const createdAt = ticket.createdAt ?? ticket.created_at;
  return {
    ...ticket,
    updatedAt,
    updated_at: updatedAt,
    createdAt,
    created_at: createdAt,
  };
}

/** Lowercase status-count keys so API variants like `Open` still map correctly.
 * Merges into `previous` so filtered responses that omit or zero other buckets
 * do not wipe sidebar / Open·Pending·Solved header counts.
 * Pass `{ replace: true }` when counts must be fully replaced
 * so legitimate zeros from an empty inbox are applied.
 */
function normalizeStatusCounts(raw, previous = defaultStatusCounts, options = {}) {
  const replace = Boolean(options.replace);
  const base = replace ? defaultStatusCounts : { ...defaultStatusCounts, ...previous };
  const next = { ...base };
  if (!raw || typeof raw !== 'object') return next;

  const entries = [];
  Object.entries(raw).forEach(([key, value]) => {
    const normalizedKey = String(key).toLowerCase();
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num)) return;
    const targetKey = normalizedKey === 'closed' ? 'solved' : normalizedKey;
    entries.push([targetKey, num]);
  });
  if (!entries.length) return next;

  // Empty group / empty inbox: API sends a full zero payload — trust it.
  // Require several keys so a single filtered `{ open: 0 }` does not wipe the sidebar.
  // On group switch, replace entirely so prior group buckets cannot linger.
  const allZero = entries.every(([, num]) => num === 0);
  if (replace || (allZero && entries.length >= 4)) {
    const replaced = { ...defaultStatusCounts };
    entries.forEach(([targetKey, num]) => {
      replaced[targetKey] = num;
    });
    return replaced;
  }

  entries.forEach(([targetKey, num]) => {
    // APIs often send 0 for inactive status buckets on filtered list responses.
    if (num === 0 && (previous[targetKey] || 0) > 0) {
      return;
    }
    next[targetKey] = num;
  });
  return next;
}

/** Stable cache key for a ticket list query (inbox tab, status, search). */
export function buildTicketListCacheKey(arg = {}) {
  if (!arg || typeof arg !== 'object') return '';
  const parts = [
    `p:${arg.page ?? 0}`,
    `l:${arg.limit ?? 10}`,
    arg.status ? `s:${arg.status}` : '',
    arg.view ? `v:${arg.view}` : '',
    arg.team_id ? `t:${arg.team_id}` : '',
    arg.search ? `q:${String(arg.search).trim()}` : '',
    arg.sort_by ? `sb:${arg.sort_by}` : '',
    arg.order ? `o:${arg.order}` : '',
  ];
  return parts.join('|');
}

function snapshotListCache(state) {
  return {
    tickets: state.tickets,
    pagination: state.pagination,
    listExhausted: state.listExhausted,
  };
}

function patchActiveListCache(state) {
  const key = state.lastFetchedListKey;
  if (!key) return;
  state.cacheByListKey[key] = snapshotListCache(state);
}

/**
 * Build list query for GET /ticket/get-ticket.
 * Ticket V2 views use `view` / `team_id` (assigned_to_me, unassigned, team, spam).
 * Legacy tabs still use `status` via activeTab when no view is set.
 * @param {URLSearchParams} searchParams
 */
export function buildTicketListParams(activeTab, searchParams, limit = 10) {
  const page = parseInt(searchParams.get('page') || '0', 10);
  const statusMap = { 1: 'open', 2: 'pending', 3: 'solved', 4: 'spam' };
  const view = searchParams.get('view') || undefined;
  const teamId = searchParams.get('team_id') || undefined;
  const statusFromUrl = searchParams.get('status') || undefined;

  let status;
  if (view === 'spam') {
    status = undefined;
  } else if (view === 'assigned_to_me') {
    status = statusFromUrl || 'open';
  } else if (view === 'unassigned' || view === 'team') {
    status = statusFromUrl || (activeTab === 0 ? undefined : statusMap[activeTab]);
  } else if (statusFromUrl) {
    status = statusFromUrl;
  } else {
    status = activeTab === 0 ? undefined : statusMap[activeTab];
  }

  const search = (searchParams.get('search') || '').trim() || undefined;

  // API: order=asc|desc; optional sort_by=updatedAt (omit for createdAt default).
  // Legacy URL keys sortField/sortOrder are mapped for compatibility.
  let sortBy = (searchParams.get('sort_by') || '').trim();
  let order = String(searchParams.get('order') || '').toLowerCase();
  if (!sortBy && !order) {
    const legacyField = (searchParams.get('sortField') || '').trim();
    const legacyOrder = String(searchParams.get('sortOrder') || '').toLowerCase();
    if (legacyField === 'updatedAt' || legacyField === 'updated_at') {
      sortBy = 'updatedAt';
    }
    if (legacyOrder === 'asc' || legacyOrder === 'desc') {
      order = legacyOrder;
    }
  }
  if (sortBy !== 'updatedAt') {
    sortBy = undefined;
  }
  order = order === 'asc' ? 'asc' : 'desc';

  return {
    page,
    limit,
    status,
    view,
    team_id: teamId || undefined,
    search,
    sort_by: sortBy,
    order,
  };
}

export const getAllTickets = createAsyncThunk(
  'tickets/getAllTickets',
  async (arg = {}, { getState, rejectWithValue }) => {
    const opts = typeof arg === 'object' && arg !== null ? arg : {};
    const {
      page = 0,
      limit = 10,
      status,
      view,
      team_id,
      search,
      sort_by,
      order,
      append = false,
    } = opts;
    const isAppend = Boolean(append);
    try {
      let signal;
      if (isAppend) {
        if (appendController) appendController.abort();
        appendController = new AbortController();
        signal = appendController.signal;
      } else {
        if (appendController) appendController.abort();
        if (controller) controller.abort();
        controller = new AbortController();
        signal = controller.signal;
      }

      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;

      // Backend scopes tickets to groups the user has joined — do not send group_id.
      const params = { page: page + 1, limit };
      if (status) {
        params.status = status;
      }
      if (view) {
        params.view = view;
      }
      if (team_id) {
        params.team_id = team_id;
      }
      const searchTerm = typeof search === 'string' ? search.trim() : '';
      if (searchTerm) {
        params.search = searchTerm;
      }
      if (sort_by) {
        params.sort_by = sort_by;
      }
      if (order) {
        params.order = order;
      }

      const response = await axios.get(`${ROOT_URL}/ticket/get-ticket`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        params,
        signal,
      });
      return response.data;
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'CanceledError') {
        return rejectWithValue({ canceled: true });
      }
      return rejectWithValue(error.response?.data || 'Failed to fetch tickets');
    }
  }
);

const getAllTicketsSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    addTicketOptimistic: (state, action) => {
      const newTicket = action.payload;
      const exists = state.tickets.some(t => t._id === newTicket._id);
      if (!exists) {
        state.tickets = [normalizeTicket(newTicket), ...state.tickets];
        patchActiveListCache(state);
      }
    },
    updateTicketOptimistic: (state, action) => {
      const updatedTicket = normalizeTicket(action.payload);
      const index = state.tickets.findIndex(t => t._id === updatedTicket._id);
      if (index !== -1) {
        state.tickets[index] = { ...state.tickets[index], ...updatedTicket };
        patchActiveListCache(state);
      }
    },
    removeTicketOptimistic: (state, action) => {
      const ticketId = action.payload;
      state.tickets = state.tickets.filter(t => t._id !== ticketId);
      patchActiveListCache(state);
    },
    /** Socket: new ticket — bump totals so tab headers stay in sync before refetch */
    adjustStatusCountsForNewTicket: (state, action) => {
      const status = String(action.payload?.status || '').toLowerCase();
      state.statusCounts.all = (state.statusCounts.all || 0) + 1;
      if (['open', 'pending', 'solved', 'spam'].includes(status)) {
        state.statusCounts[status] = (state.statusCounts[status] || 0) + 1;
      }
    },
    /** Socket: status changed — move counts between buckets (`all` unchanged) */
    adjustStatusCountsForStatusChange: (state, action) => {
      const oldS = String(action.payload?.oldStatus || '').toLowerCase();
      const newS = String(action.payload?.newStatus || '').toLowerCase();
      if (!oldS || !newS || oldS === newS) return;
      const keys = ['open', 'pending', 'solved', 'spam'];
      if (keys.includes(oldS)) {
        state.statusCounts[oldS] = Math.max(0, (state.statusCounts[oldS] || 0) - 1);
      }
      if (keys.includes(newS)) {
        state.statusCounts[newS] = (state.statusCounts[newS] || 0) + 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllTickets.pending, (state, action) => {
        const append = Boolean(action.meta?.arg?.append);
        const silent = Boolean(action.meta?.arg?.silent) || append;
        const key = buildTicketListCacheKey(action.meta?.arg);
        const cached = !append && key ? state.cacheByListKey[key] : null;
        const softLoad = Boolean(cached);

        if (append) {
          state.listLoadingMore = true;
        } else {
          state.listExhausted = false;
          state.listLoadingMore = false;
          if (cached) {
            state.tickets = cached.tickets;
            state.pagination = cached.pagination;
            state.listExhausted = cached.listExhausted;
            state.initialLoaded = true;
            state.lastFetchedListKey = key;
            state.loading = false;
            // Do not paint a stale header count from another filter while we
            // revalidate; TicketList shows a skeleton until fulfilled.
            if (!silent) {
              state.statusCountRefreshRequests += 1;
            }
          } else if (!silent) {
            state.loading = true;
            state.tickets = [];
            state.initialLoaded = false;
          }
        }

        if (!silent && !softLoad) {
          state.blockingListRequests += 1;
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(getAllTickets.fulfilled, (state, action) => {
        const append = Boolean(action.meta?.arg?.append);
        const silent = Boolean(action.meta?.arg?.silent) || append;
        const softRefresh = !append && !silent;
        const limit = action.meta?.arg?.limit ?? 10;
        state.listLoadingMore = false;
        if (!silent && state.blockingListRequests > 0) {
          state.blockingListRequests = Math.max(0, state.blockingListRequests - 1);
          if (state.blockingListRequests === 0) {
            state.loading = false;
          }
        }
        if (softRefresh && state.statusCountRefreshRequests > 0) {
          state.statusCountRefreshRequests = Math.max(
            0,
            state.statusCountRefreshRequests - 1
          );
        }
        state.initialLoaded = true;
        const raw = (action.payload?.data || []).map(normalizeTicket);
        const p = action.payload?.pagination
          ? { ...action.payload.pagination }
          : defaultPagination;

        if (append) {
          const existingIds = new Set(state.tickets.map((t) => t._id));
          const merged = [...state.tickets];
          for (const t of raw) {
            if (t._id && !existingIds.has(t._id)) {
              merged.push(t);
              existingIds.add(t._id);
            }
          }
          state.tickets = merged;
        } else {
          state.tickets = raw;
        }

        state.pagination = p;
        const reqStatus = action.meta?.arg?.status;
        const reqView = action.meta?.arg?.view;
        // Scoped inboxes (assigned_to_me, unassigned, team, spam) return list totals /
        // statusCounts for that subset. Never fold those into global sidebar buckets
        // (esp. solved) — All Solved ≠ Assigned-to-me Solved.
        const isScopedView = Boolean(reqView);

        // Keep previous counts when the payload omits statusCounts (common on append /
        // filtered views) so the header does not flash 0 or a stale pagination total.
        if (
          !isScopedView &&
          action.payload?.statusCounts &&
          typeof action.payload.statusCounts === 'object' &&
          Object.keys(action.payload.statusCounts).length > 0
        ) {
          state.statusCounts = normalizeStatusCounts(
            action.payload.statusCounts,
            state.statusCounts
          );
        } else if (
          isScopedView &&
          action.payload?.statusCounts &&
          typeof action.payload.statusCounts === 'object' &&
          Object.keys(action.payload.statusCounts).length > 0
        ) {
          // Only refresh inbox-level keys; leave open/pending/solved/closed alone.
          const inboxKeys = new Set([
            'all',
            'assigned_to_me',
            'unassigned',
            'spam',
          ]);
          const inboxOnly = {};
          Object.entries(action.payload.statusCounts).forEach(([key, value]) => {
            const normalizedKey = String(key).toLowerCase();
            if (!inboxKeys.has(normalizedKey)) return;
            const num = typeof value === 'number' ? value : Number(value);
            if (!Number.isFinite(num)) return;
            inboxOnly[normalizedKey] = num;
          });
          if (Object.keys(inboxOnly).length > 0) {
            state.statusCounts = {
              ...state.statusCounts,
              ...inboxOnly,
            };
          }
        }

        // Header count for the active filter must match the filtered list. APIs often
        // omit that key or send 0 while pagination.total is the real filtered total.
        // Only apply on the unscoped All inbox — scoped views must not overwrite
        // global open/pending/solved used by the sidebar.
        if (
          !isScopedView &&
          (reqStatus === 'open' ||
            reqStatus === 'pending' ||
            reqStatus === 'solved') &&
          typeof p.total === 'number' &&
          p.total >= 0
        ) {
          state.statusCounts = {
            ...state.statusCounts,
            [reqStatus]: p.total,
          };
        }

        // Prefer short/empty page detection over pagination.total — filtered views
        // (e.g. assigned_to_me) often return a total that matches the first page size.
        if (append && !raw.length) {
          state.listExhausted = true;
        } else if (raw.length < limit) {
          state.listExhausted = true;
        } else {
          state.listExhausted = false;
        }

        const cacheKey = buildTicketListCacheKey(action.meta?.arg);
        if (!append && cacheKey) {
          state.cacheByListKey[cacheKey] = snapshotListCache(state);
          state.lastFetchedListKey = cacheKey;
        }
      })
      .addCase(getAllTickets.rejected, (state, action) => {
        const append = Boolean(action.meta?.arg?.append);
        const silent = Boolean(action.meta?.arg?.silent) || append;
        const softRefresh = !append && !silent;
        const cacheKey = buildTicketListCacheKey(action.meta?.arg);
        const hasCache = Boolean(cacheKey && state.cacheByListKey[cacheKey]);
        if (append) {
          state.listLoadingMore = false;
        }
        if (softRefresh && state.statusCountRefreshRequests > 0) {
          state.statusCountRefreshRequests = Math.max(
            0,
            state.statusCountRefreshRequests - 1
          );
        }
        if (action.payload?.canceled) {
          if (!silent && state.blockingListRequests > 0) {
            state.blockingListRequests = Math.max(0, state.blockingListRequests - 1);
            if (state.blockingListRequests === 0) {
              state.loading = false;
            }
          }
          return;
        }
        if (!silent && !hasCache && state.blockingListRequests > 0) {
          state.blockingListRequests = Math.max(0, state.blockingListRequests - 1);
          if (state.blockingListRequests === 0) {
            state.loading = false;
          }
        }
        if (!hasCache) {
          state.error = action.payload?.message || 'Failed to fetch tickets';
        }
      });
  },
});

export const {
  addTicketOptimistic,
  updateTicketOptimistic,
  removeTicketOptimistic,
  adjustStatusCountsForNewTicket,
  adjustStatusCountsForStatusChange,
} = getAllTicketsSlice.actions;

export default getAllTicketsSlice.reducer;
